import { assign, Machine } from 'xstate';
import { isProduction, reportError } from '@pypestream/utils';
import { i18n } from '@pypestream/translations';

import {
  GLOBAL_APP_SUB_MACHINE_IDS,
  sendUserMessage,
  showToastMessage,
} from './app.xstate-utils';
import { getXstateUtils } from './xstate.utils';
import { userService } from './app.xstate';
import { recordError } from './record-error';
import { WORKFLOWS_NS } from '../utils/i18n-namespaces';
import { applySwitchedSolutionType } from '../utils/apply-switched-solution-type';
import {
  acceptSolutionOpen,
  clearedSolutionOpenState,
  failureFromLoadedSolution,
  failureFromOpenAttempt,
  failureHeldForSelect,
  isLatchedFailureReveal,
  solutionOpenLatchForProjectChange,
} from '../utils/solution-open-failure';
import { clearedSolutionScope } from './solution-scope';
import { invalidateChannelDerivedCaches } from './invalidate-channel-derived-caches';
import { markChannelSettingsLoad } from './channel-settings-load-status';
import { contextAfterProjectAccessGranted } from '../utils/access-setup';
import {
  ProStudioContext,
  ProStudioEvents,
  ProStudioTypestates,
  ProStudioState,
} from './prostudio.xstate.types';
import {
  changeBotType,
  createBot,
  deleteAsset,
  deleteBot,
  downloadVersionFile,
  loadBots,
  loadBotVersions,
  publishBotVersion,
  loadPublishModalData,
  deleteScript,
  getFileData,
  validateVersion,
  loadBotPypes,
  deleteChannel,
  createChannel,
  updateChannel,
  getProjectData,
  getUserData,
  updateAssets,
  updateSolution,
  refetchSolutionData,
  loadResourceLibraryData,
  loadActionNodeNames,
  reloadNodes,
  initFeatureFlags,
  setSelectedSolution,
  identifyLaunchDarklyContext,
  updateSolutionName,
  toggleAnalyticsForChannel,
  loadCustomerPypes,
  createLiveAgentPlatform,
  updateLiveAgentPlatform,
  deleteLiveAgentPlatform,
  loadApiCredentials,
  loadChannelApiCredentials,
  createApiCredential,
  updateApiCredential,
  deleteApiCredential,
  loadErrorHandling,
  updateErrorHandling,
  loadDefaultMessages,
  updateDefaultMessages,
  loadPypeRuleGroups,
  createPypeRuleGroup,
  updatePypeRuleGroup,
  deletePypeRuleGroup,
  assignPypeRuleGroup,
  assignPypeConnection,
} from './prostudio.xstate.invokes';
import {
  downloadPythonFile,
  isProStudioViewer,
  isSolutionNotFound,
  removeSolutionFromList,
} from '../utils';
import { applyDeploymentHistory } from '../utils/apply-deployment-history';
import { ErrorTags } from '@pypestream/ui-patterns';
import { getAnalytics } from '../hooks/use-analytics';
import { tableStore } from '../stores';

const {
  createInvokablePromise,
  createXstateHooks: createProStudioXstateHooks,
} = getXstateUtils<
  ProStudioContext,
  ProStudioEvents,
  ProStudioTypestates,
  ProStudioState
>();

export { createProStudioXstateHooks };

const entryLogsAvalable = !isProduction;

const logOnEntry = ({
  name,
  info,
  color = 'purple',
}: {
  name: string;
  info?: unknown;
  color?: string;
}) => {
  if (!entryLogsAvalable) return;
  console.log(
    `%cstate: ${name}`,
    `color: ${color}; font-size: larger; font-weight: bold`,
    info || ''
  );
};

const initialBotRelatedContext = {
  botId: undefined,
  botName: undefined,
  botIdURLParam: undefined,
  channelId: undefined,
  deployedVersions: null,
  draft: null,
  draftVersion: null,
  solutionVersionPsId: null,
  solutionVersionPsSolutionId: null,
  envs: undefined,
  botPypes: null,
  // Solution-scoped: the Default Messages page loads once while this is null and
  // reads the channels of the solution in ctx, so a copy kept across `selectBot`
  // would show the previous solution's channels (PSTUDIO-4553). Of the slices
  // `invalidateChannelDerivedCaches` drops, this is the one known to be read per
  // solution; `liveAgentPypes` and `apiCredentials` below were already reset here
  // for their own reasons, and the rest were not checked.
  defaultMessagesPypes: null,
  liveAgentPypes: null,
  agentRoutingSchemas: null,
  apiCredentials: null,
  errorHandling: null,
  selectedBot: undefined,
  surveyLinkedSolutions: undefined,
  isLoadingPublishModalData: false,
  publishModalSolutionEnvs: undefined,
  draftVersionSolutionData: null,
  solutionVersions: [],
  corpusSettings: null,
  // initial state for versions should be undefined (not empty array) because we are not loading the data yet
  versions: undefined,
};

const initialContext: ProStudioContext = {
  ...initialBotRelatedContext,
  customerId: undefined,
  credentials: undefined,
  providers: undefined,
  project: undefined,
  orgName: 'Pypestream',
  isSwitchingSolution: false,
  solutionOpenFailure: null,
  solutionOpenAttempt: null,
  solutionOpenFailureDismissed: false,
  // initial state for bots should be undefined (not empty array) because we are not loading the data yet
  solutions: undefined,
  userInfo: {},
  userProjects: undefined,
  projectTools: undefined,
  projectUsers: undefined,
  errors: [],
  debugMode: false,
  presencePagePath: undefined,
  presenceSolutionId: undefined,
  solutionUserPresences: undefined,
};

export const proStudioMachine = Machine<
  ProStudioContext,
  ProStudioState,
  ProStudioEvents
>({
  id: GLOBAL_APP_SUB_MACHINE_IDS.proStudio,
  predictableActionArguments: true,
  preserveActionOrder: true,
  context: initialContext,
  invoke: [
    {
      id: 'initializing-feature-flags',
      src: initFeatureFlags,
    },
    {
      id: 'userDataObserver',
      src: getUserData,
    },
    {
      id: 'identifyingLaunchDarklyContext',
      src: identifyLaunchDarklyContext,
    },
  ],
  on: {
    setFeatureFlags: {
      actions: assign((ctx, { values }) => {
        ctx.featureFlags = values;
        tableStore.setFeatureFlags(values);
        return ctx;
      }),
    },
    // Root-level (not nested under `idle`) so every state the machine can be
    // parked in can still service a copy-to-clipboard request from the UI —
    // an idle-only handler dropped the event silently both when a load was
    // running (e.g. Channel Settings' load chain on mount, which keeps Save
    // enabled, PSTUDIO-4354) and when the machine was parked in
    // `solutionNotFound` (PSTUDIO-3739).
    copyToClipboard: {
      actions: [
        async (ctx, event) => {
          logOnEntry({
            name: 'copyToClipboard',
            info: { ctx, event },
            color: 'cyan',
          });

          try {
            await navigator.clipboard.writeText(event.value);
            sendUserMessage({
              type: 'success',
              text: event.message,
              autoClose: 3000,
            });
          } catch (error) {
            sendUserMessage({
              type: 'error',
              text:
                i18n.t('pro-studio/common:clipboard.copyError', {
                  defaultValue: 'Could not copy to clipboard',
                }) || '',
              autoClose: 3000,
            });
            // Shared by every copy-to-clipboard call site (the Project ID and
            // DuploCloud ID buttons today) — without this, a rejected
            // `navigator.clipboard.writeText` (denied permission, insecure
            // context, unfocused document) is an error the user sees and
            // Sentry never does. Guarded, same reasoning as `showError` above.
            try {
              reportError(error, {
                source: 'copyToClipboard',
                tags: { app: 'pro-studio' },
              });
            } catch {
              // Ignored on purpose — see above.
            }
          }
        },
      ],
    },
    updateUserInfo: {
      target: '#checkingUserInfo',
      actions: assign((ctx, event) => {
        logOnEntry({
          name: 'updateUserInfo',
          info: { ctx, event },
          color: 'cyan',
        });
        const {
          data: { userProjects, ...userInfo },
        } = event;
        ctx.userInfo = userInfo;
        ctx.userProjects = userProjects;
        ctx.userProjectsLoadFailed = false;
        return ctx;
      }),
    },
    // No `target`: only the flag the retry screen reads. The machine stays
    // wherever it is parked (`#checkingUserInfo`, waiting for `updateUserInfo`).
    setUserProjectsLoadFailed: {
      actions: assign((ctx, event) => {
        ctx.userProjectsLoadFailed = event.failed;
        return ctx;
      }),
    },
    // No `target`: a file-list refresh must not re-enter any loading state. Ignored for
    // a version the editor is no longer on — a list that resolves after a solution or
    // draft switch describes the one that was left.
    setDraftScriptFiles: {
      actions: assign((ctx, event) => {
        if (
          event.type !== 'setDraftScriptFiles' ||
          !ctx.draft ||
          event.versionName !== ctx.draftVersion
        ) {
          return ctx;
        }
        return { ...ctx, draft: { ...ctx.draft, files: event.files } };
      }),
    },
    /**
     * Drop the selected solution when the URL no longer names one.
     *
     * The URL is the source of truth for "which solution is selected", and the
     * project-level routes (`/:projectId`, `/:projectId/settings/*`,
     * `/:projectId/configurations`) name none. `setParams` can only ever SET
     * (`event.payload.botName || ctx.botName`), so leaving a solution for Project
     * Settings via client-side navigation kept the previous one in context and the
     * breadcrumb still showed it. A hard reload of the same URL looked correct only
     * because context starts empty there. (The Reporting API Credential modal used
     * to silently scope its channel list by the same stale handle; as of
     * PSTUDIO-3829 the user picks the solution explicitly.)
     *
     * Clears the URL-scope handles, plus `defaultMessagesPypes` (PSTUDIO-4553): the
     * rest of the solution's loaded data is left alone, since navigating back into
     * it re-enters `#checkingUserInfo` (the route sync sees `botName` change from
     * empty) and reloads it anyway. That re-entry does NOT reload Default Messages,
     * whose page loads once while the slice is null, so the slice goes with the
     * handles. See `clearedSolutionScope` for the exact list.
     *
     * Deliberately has NO `target`: re-entering `#checkingUserInfo` here would
     * refetch the project on every project-level navigation. Dispatched from
     * `syncXstateWithRouteParams`.
     */
    clearSolutionScope: {
      actions: assign((ctx, event) => {
        logOnEntry({
          name: 'clearSolutionScope',
          info: { ctx, event },
          color: 'cyan',
        });

        return {
          ...ctx,
          ...clearedSolutionScope(),
          ...clearedSolutionOpenState(),
        };
      }),
    },
    dismissSolutionOpenFailure: {
      actions: assign({
        solutionOpenFailureDismissed: true,
      }),
    },
    setParams: [
      {
        actions: assign((ctx, event) => {
          if (event.type !== 'setParams') {
            return ctx;
          }

          logOnEntry({
            name: 'setParams',
            info: { ctx, event },
            color: 'cyan',
          });

          const latch = solutionOpenLatchForProjectChange({
            currentProjectId: ctx.project?.id,
            nextProjectId: event.payload.projectId,
            failure: ctx.solutionOpenFailure,
            attempt: ctx.solutionOpenAttempt,
            dismissed: ctx.solutionOpenFailureDismissed,
          });

          return {
            ...ctx,
            ...latch,
            project: {
              ...ctx.project,
              id: event.payload.projectId || ctx.project?.id || '',
              name: ctx.project?.name || '',
              createdAt: ctx.project?.createdAt || '',
              accountId: ctx.project?.accountId || '',
            },
            botName: event.payload.botName || ctx.botName || '',
            channelId: event.payload.channelId || ctx.channelId,
            debugMode: event.payload.debugMode || ctx.debugMode,
            botIdURLParam:
              event.payload.botIdURLParam || ctx.botIdURLParam || '',
          };
        }),
        target: `#checkingUserInfo`,
        cond: (ctx, event) =>
          !ctx.isSwitchingSolution &&
          (!!event.payload.botName ||
            !!event.payload.projectId ||
            !!event.payload.channelId),
      },
      {
        actions: assign((ctx, event) => {
          if (event.type !== 'setParams') {
            return ctx;
          }

          const latch = solutionOpenLatchForProjectChange({
            currentProjectId: ctx.project?.id,
            nextProjectId: event.payload.projectId,
            failure: ctx.solutionOpenFailure,
            attempt: ctx.solutionOpenAttempt,
            dismissed: ctx.solutionOpenFailureDismissed,
          });

          return {
            ...ctx,
            ...latch,
            project: {
              ...ctx.project,
              id: event.payload.projectId || ctx.project?.id || '',
              name: ctx.project?.name || '',
              createdAt: ctx.project?.createdAt || '',
              accountId: ctx.project?.accountId || '',
            },
            botName: event.payload.botName || ctx.botName || '',
            channelId: event.payload.channelId || ctx.channelId,
            debugMode: event.payload.debugMode || ctx.debugMode,
            botIdURLParam:
              event.payload.botIdURLParam || ctx.botIdURLParam || '',
          };
        }),
        cond: (ctx, event) =>
          !!ctx.isSwitchingSolution &&
          (!!event.payload.botName ||
            !!event.payload.projectId ||
            !!event.payload.channelId),
      },
    ],
    showError: [
      {
        actions: [
          (ctx, event) => {
            logOnEntry({
              name: 'showError',
              info: { ctx, event },
              color: 'cyan',
            });
            sendUserMessage({
              type: 'error',
              text: event.error,
              autoClose: 3000,
            });
            // The SECOND choke point, beside the one in `xstate.utils.ts`.
            // That one covers every rejected invoke; this one covers every
            // error a screen raises by DISPATCHING instead of throwing — the
            // channel forms, the voice form, pype-designer and the file
            // uploads all reach the user through here. Without it those are
            // the errors a user sees and Sentry never does.
            //
            // Guarded, because reporting must not be able to break the path
            // that shows the user what went wrong.
            try {
              reportError(new Error(event.error), {
                source: 'showError',
                tags: { app: 'pro-studio' },
              });
            } catch {
              // Ignored on purpose — see above.
            }
          },
        ],
      },
    ],
    startPresence: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'startPresence') return {};
        return {
          presenceSolutionId: event.solutionId,
          presencePagePath: event.pagePath,
        };
      }),
    },
    stopPresence: {
      actions: assign({
        presenceSolutionId: undefined,
        presencePagePath: undefined,
        solutionUserPresences: undefined,
      }),
    },
    presenceRouteChange: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'presenceRouteChange') return {};
        return { presencePagePath: event.pagePath };
      }),
    },
    setPresenceData: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'setPresenceData') return {};
        return { solutionUserPresences: event.presences };
      }),
    },
    // Pushed in by useSolutionDeployments' subscription (and cleared by it when the open
    // solution changes), so the publish toast and the Versions environment cards read one
    // shared, always-current set of rows instead of subscribing separately.
    setSolutionDeployments: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'setSolutionDeployments') return {};
        return { solutionDeployments: event.deployments };
      }),
    },
    // Pushed in by useLiveDeploymentHistory whenever the feed above reports a new attempt
    // or step, so the Versions table follows a publish live, in every tab (PSTUDIO-4354).
    setDeploymentHistory: {
      actions: assign(applyDeploymentHistory),
    },
    // PSTUDIO-3671: a global transition so any React trigger (cross-tab reload,
    // coworker subscription, solution-deleted broadcast) can eject to the
    // not-found screen regardless of the current nested state. XState v4 drops
    // events with no transition in the active state — a root `on` avoids that.
    solutionNotFound: {
      target: '#solutionNotFound',
    },
  },
  states: {
    studio_idle: {
      id: 'studio_idle',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'studio_idle',
          info: { ctx, event },
        });
      },
    },
    checkingUserInfo: {
      id: 'checkingUserInfo',
      initial: 'checkAccess',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'checkingUserInfo',
          info: { ctx, event },
        });
      },
      states: {
        checkAccess: {
          id: 'checkAccess',
          entry: (ctx, event) => {
            logOnEntry({
              name: 'checkAccess',
              info: { ctx, event },
            });
          },
          always: [
            {
              target: '#accessDenied',
              cond: (ctx, _event) => {
                const projectId = ctx.project?.id;

                const res =
                  Boolean(
                    ctx.userProjects !== undefined &&
                      projectId &&
                      !ctx.userProjects?.map(({ id }) => id).includes(projectId)
                  ) ||
                  Boolean(
                    ctx.userProjects && !ctx.userProjects.length && !projectId
                  );

                return res;
              },
            },
            {
              target: '#projectFlow',
              cond: (ctx, _event) =>
                Boolean(
                  ctx.customerId === undefined &&
                    ctx.userInfo?.id !== undefined &&
                    ctx.userProjects &&
                    ctx.project?.id
                ),
            },
            {
              target: '#botFlow',
              cond: (ctx, _event) =>
                !!ctx.userInfo?.id &&
                !!ctx.customerId &&
                !!ctx.project?.id &&
                !!ctx.botName,
            },
            {
              target: '#studio_idle',
              cond: (ctx, _event) => !ctx.userInfo?.id || !ctx.customerId,
            },
          ],
        },
        projectFlow: {
          id: 'projectFlow',
          initial: 'loadingProjectData',
          entry: (ctx, event) => {
            logOnEntry({
              name: 'projectFlow',
              info: { ctx, event },
            });
          },
          states: {
            loadingProjectData: {
              id: 'loadingProjectData',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'loadingProjectData',
                  info: { ctx, event },
                });
              },
              invoke: createInvokablePromise({
                src: getProjectData,
                onDoneTarget: '#validatingProjectId',
                onErrorTarget: '#error',
                onDoneAssignContext({ ctx, data }) {
                  if (data) {
                    ctx.customerId = data.id;
                    ctx.project = data.project;
                    ctx.credentials = data.credentials;
                    ctx.providers = data.providers;
                    ctx.projectUsers = data.projectUsers;

                    const userProject = ctx.userProjects?.find(
                      (p) => p.id === data.project?.id
                    );
                    tableStore.setIsViewer(
                      isProStudioViewer(
                        userProject?.role ? [userProject.role] : []
                      )
                    );
                    ctx.projectTools = userProject?.availableTools;
                  }
                },
                onErrorAssignContext({ ctx, error }) {
                  recordError(ctx.errors, error);
                },
                onErrorActions: [
                  showToastMessage({
                    text: 'Error fetching customer data',
                  }),
                ],
              }),
            },
            validatingProjectId: {
              id: 'validatingProjectId',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'validatingProjectId',
                  info: { ctx, event },
                });
              },
              always: [
                {
                  target: '#accessDenied',
                  cond: (ctx, _event) => {
                    const projectId = ctx.project?.id;
                    const res =
                      Boolean(
                        ctx.userProjects !== undefined &&
                          projectId &&
                          !ctx.userProjects
                            ?.map(({ id }) => id)
                            .includes(projectId)
                      ) ||
                      Boolean(
                        ctx.userProjects &&
                          !ctx.userProjects.length &&
                          !projectId
                      );
                    return res;
                  },
                },
                {
                  target: '#projectDataLoaded',
                  cond: (ctx, _event) =>
                    Boolean(ctx.userInfo?.id !== undefined && ctx.userProjects),
                },
              ],
            },
            projectDataLoaded: {
              id: 'projectDataLoaded',
              initial: 'checkingRequiredData',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'projectDataLoaded',
                  info: { ctx, event },
                });
              },
              // Channel mutations are only handled under `idle`, so they bubble
              // to here whenever a botFlow is running and would otherwise be
              // dropped silently — settling neither cb nor errorCb. A caller
              // awaiting that promise (the unsaved-changes guard) would then wait
              // forever, so settle the failure channel instead of swallowing.
              on: {
                createChannel: {
                  actions: (_ctx, event) => {
                    if (event.type !== 'createChannel') return;
                    event.payload.errorCb?.();
                  },
                },
                updateChannel: {
                  actions: (_ctx, event) => {
                    if (event.type !== 'updateChannel') return;
                    event.payload.errorCb?.();
                  },
                },
              },
              states: {
                checkingRequiredData: {
                  id: 'checkingRequiredData',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'checkingRequiredData',
                      info: { ctx, event },
                    });
                  },
                  always: [
                    {
                      target: '#loadingBots',
                      cond: (ctx, _event) => !ctx.solutions,
                    },
                    // PSTUDIO-3671: the URL points at a solution that is absent
                    // from the freshly-loaded list => it was deleted (a still-
                    // provisioning solution WOULD be present). Eject to All
                    // Solutions instead of hanging on a stale/loading editor.
                    // Placed after the `!solutions` guard so the list is loaded.
                    {
                      target: '#solutionNotFound',
                      cond: (ctx, _event) =>
                        isSolutionNotFound({
                          solutions: ctx.solutions,
                          botName: ctx.botName,
                          botIdURLParam: ctx.botIdURLParam,
                        }),
                    },
                    {
                      target: '#botFlow',
                      cond: (ctx, _event) =>
                        !!ctx.botName &&
                        !ctx.draft &&
                        !ctx.versions &&
                        !ctx.deployedVersions &&
                        !ctx.selectedBot &&
                        !ctx.envs,
                    },
                    {
                      actions: [
                        assign((ctx, event) => {
                          logOnEntry({
                            name: 'setBotName',
                            info: { ctx, event },
                            color: 'cyan',
                          });

                          const botName = ctx.solutions?.find(
                            (solution) => solution.id === ctx.botIdURLParam
                          )?.name;

                          return {
                            ...ctx,
                            botName,
                            botIdURLParam: undefined,
                          };
                        }),
                      ],
                      cond: (ctx, _event) =>
                        !ctx.botName && !!ctx.solutions && !!ctx.botIdURLParam,
                      target: '#projectDataLoaded',
                    },
                    {
                      target: '#idle',
                    },
                  ],
                },
                idle: {
                  id: 'idle',
                  initial: 'idleView',
                  entry: [
                    (ctx, event) => {
                      logOnEntry({
                        name: 'idle',
                        info: { ctx, event },
                        color: 'green',
                      });
                    },
                    assign({ isSwitchingSolution: false }),
                  ],
                  on: {
                    selectBot: [
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'selectBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            const heldFailure = failureHeldForSelect({
                              current: ctx.solutionOpenFailure,
                              requestedId: event.id,
                            });

                            return {
                              ...ctx,
                              ...initialBotRelatedContext,
                              isSwitchingSolution: true,
                              solutionOpenFailure: heldFailure,
                              solutionOpenAttempt: {
                                solutionId: event.id,
                                displayName: event.name,
                              },
                              solutionOpenFailureDismissed: heldFailure
                                ? ctx.solutionOpenFailureDismissed
                                : false,
                            };
                          }),
                        ],
                        cond: (ctx, event) =>
                          acceptSolutionOpen({
                            selectedDisplayName: ctx.selectedBot?.displayName,
                            requestedName: event.name,
                            requestedId: event.id,
                            openFailureId: ctx.solutionOpenFailure?.solutionId,
                            force: event.force,
                          }),
                        target: '#setSelectedSolution',
                      },
                      {
                        actions: assign({
                          solutionOpenFailureDismissed: false,
                        }),
                        cond: (ctx, event) =>
                          isLatchedFailureReveal({
                            openFailureId: ctx.solutionOpenFailure?.solutionId,
                            requestedId: event.id,
                            force: event.force,
                          }),
                      },
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'selectBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                            if (event.cb && event.id) {
                              event.cb(`/${ctx.project?.id}/${event.id}`);
                            }

                            return ctx;
                          }),
                        ],
                        cond: (ctx, event) =>
                          ctx.selectedBot?.displayName === event.name,
                        target: '#idle',
                      },
                    ],
                    createCustomerBot: [
                      {
                        target: '#creatingBotFlow',
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'createCustomerBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            return {
                              ...ctx,
                              ...initialBotRelatedContext,
                              // Creating a solution is a different gesture. The
                              // previous open failure must not stay on screen.
                              ...clearedSolutionOpenState(),
                            };
                          }),
                        ],
                      },
                    ],
                    deleteBot: [
                      {
                        target: '#deletingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    publishBot: [
                      {
                        target: '#publishingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'publishBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadPublishModalData: [
                      {
                        target: '#loadingPublishModalDataFlow',
                        actions: [
                          // Clear the previous survey's data as a fresh load starts.
                          // Otherwise, if this load is interrupted or fails, the modal
                          // could pre-select/enable a different survey's linked mains
                          // (stale data-integrity risk — PSTUDIO-2081).
                          assign({
                            isLoadingPublishModalData: (_ctx) => true,
                            surveyLinkedSolutions: (_ctx) => undefined,
                            publishModalSolutionEnvs: (_ctx) => undefined,
                          }),
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadPublishModalData',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    setBotType: [
                      {
                        target: '#updatingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'setBotType',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateSolution: [
                      {
                        target: '#updatingSolutionFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateSolution',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    refetchSolutionData: [
                      {
                        target: '#refetchingSolutionDataFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'refetchSolutionData',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    reloadCurrentBotData: [
                      {
                        target: '#botDataLoading',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'reloadCurrentBotData',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                          assign({ selectedBot: undefined }),
                        ],
                      },
                    ],
                    getVersionFile: [
                      {
                        target: '#downloadingVersionFileFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'getVersionFile',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteAsset: [
                      {
                        target: '#deleteAssetFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteAsset',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateAssets: [
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'updateAssets',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                            return {
                              ...ctx,
                              selectedBot: event.data
                                ? { ...ctx.selectedBot, ...event.data }
                                : ctx.selectedBot,
                            };
                          }),
                        ],
                        target: '#updatingAssetsFlow',
                      },
                    ],
                    updateDraftData: [
                      {
                        target: '#idle',
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'updateDraftData',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            sendUserMessage({
                              type: 'success',
                              text: `Script ${
                                event.action === 'update'
                                  ? 'updated'
                                  : 'uploaded'
                              } successfully`,
                              autoClose: 3000,
                            });

                            return {
                              ...ctx,
                              draft: event.draft,
                              draftVersion:
                                event.draft?.version ?? ctx.draftVersion,
                            };
                          }),
                        ],
                      },
                    ],
                    deleteScript: {
                      target: '#deletingScriptFlow',
                      actions: [
                        (ctx, event) => {
                          logOnEntry({
                            name: 'deleteScript',
                            info: { ctx, event },
                            color: 'cyan',
                          });
                        },
                      ],
                    },
                    editScript: [
                      {
                        target: `#loadingScriptFlow`,
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'editScript',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    downloadScript: [
                      {
                        target: `#loadingScriptFlow`,
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'downloadScript',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    createChannel: [
                      {
                        target: '#creatingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'createChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateChannel: [
                      {
                        target: '#updatingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteChannel: [
                      {
                        target: '#deletingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadLiveAgentPlatforms: [
                      {
                        target: '#loadingLiveAgentPlatformsFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadLiveAgentPlatforms',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    createLiveAgentPlatform: [
                      {
                        target: '#creatingLiveAgentPlatformFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'createLiveAgentPlatform',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateLiveAgentPlatform: [
                      {
                        target: '#updatingLiveAgentPlatformFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateLiveAgentPlatform',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteLiveAgentPlatform: [
                      {
                        target: '#deletingLiveAgentPlatformFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteLiveAgentPlatform',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadPypeRuleGroups: [
                      {
                        target: '#loadingPypeRuleGroupsFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadPypeRuleGroups',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    createPypeRuleGroup: [
                      {
                        target: '#creatingPypeRuleGroupFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'createPypeRuleGroup',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updatePypeRuleGroup: [
                      {
                        target: '#updatingPypeRuleGroupFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updatePypeRuleGroup',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deletePypeRuleGroup: [
                      {
                        target: '#deletingPypeRuleGroupFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deletePypeRuleGroup',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    assignPypeRuleGroup: [
                      {
                        target: '#assigningPypeRuleGroupFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'assignPypeRuleGroup',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    assignPypeConnection: [
                      {
                        target: '#assigningPypeConnectionFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'assignPypeConnection',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadChannelApiCredentials: [
                      {
                        target: '#loadingChannelApiCredentialsFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadChannelApiCredentials',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadApiCredentials: [
                      {
                        target: '#loadingApiCredentialsFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadApiCredentials',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    createApiCredential: [
                      {
                        target: '#creatingApiCredentialFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'createApiCredential',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateApiCredential: [
                      {
                        target: '#updatingApiCredentialFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateApiCredential',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteApiCredential: [
                      {
                        target: '#deletingApiCredentialFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteApiCredential',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadErrorHandling: [
                      {
                        target: '#loadingErrorHandlingFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadErrorHandling',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateErrorHandling: [
                      {
                        target: '#updatingErrorHandlingFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateErrorHandling',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    loadDefaultMessages: [
                      {
                        target: '#loadingDefaultMessagesFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'loadDefaultMessages',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateDefaultMessages: [
                      {
                        target: '#updatingDefaultMessagesFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateDefaultMessages',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    analyticsToggle: [
                      {
                        target: '#togglingAnalyticsFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'analyticsToggle',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    reloadNodes: [
                      {
                        // PSTUDIO-3610: a background cross-tab sync reloads the
                        // table silently — route it to reloadingNodesFlow so it
                        // does NOT show the "CSV successfully imported" toast.
                        target: '#reloadingNodesFlow',
                        cond: (_ctx, event) =>
                          event.type === 'reloadNodes' &&
                          Boolean(event.payload.silent),
                      },
                      {
                        // Default (CSV upload): keep the existing success toast.
                        target: '#uploadingCsvFileFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'uploadingCsvFileFlow',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateSolutionName: [
                      {
                        target: '#updatingSolutionNameFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateSolutionName',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                  },
                  states: {
                    idleView: {},
                  },
                },
                loadingBots: {
                  id: 'loadingBots',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'loadingBots',
                      info: { ctx, event },
                    });
                  },
                  invoke: createInvokablePromise({
                    src: loadBots,
                    onDoneTarget: '#checkingRequiredData',
                    onErrorTarget: '#error',
                    onDoneAssignContext({ ctx, data }) {
                      if (data) {
                        ctx.solutions =
                          data.solutions as ProStudioContext['solutions'];
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      recordError(ctx.errors, error);
                    },
                    onErrorActions: [
                      showToastMessage({
                        text: 'Error fetching solutions and customer pypes data',
                      }),
                    ],
                  }),
                },
                botFlow: {
                  id: 'botFlow',
                  initial: 'botDataLoading',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'botFlow',
                      info: { ctx, event },
                    });
                  },
                  states: {
                    botDataLoading: {
                      id: 'botDataLoading',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botDataLoading',
                          info: { ctx, event },
                        });
                      },
                      type: 'parallel',
                      onDone: {
                        target: '#validatingDraftFlow',
                      },
                      states: {
                        loadingResourceLibraryData: {
                          id: 'loadingResourceLibraryData',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingResourceLibraryData',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingResourceLibraryData.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadResourceLibraryData,
                                onDoneTarget: '#loadedResourceLibraryData',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  recordError(ctx.errors, error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.resourceLibraryData =
                                      data.resourceLibraryData;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error fetching resource library data',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedResourceLibraryData',
                              type: 'final',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadedResourceLibraryData.loaded',
                                  info: { ctx, event },
                                });
                              },
                            },
                          },
                        },
                        // PSTUDIO-2998: load the dynamic native action-node names
                        // (system + library). The invoke never rejects (it resolves
                        // to a fallback) and is time-bounded (5s timeout inside the
                        // invoke), so a slow/failed backend degrades to the fallback
                        // rather than stalling this parallel region's onDone (which
                        // gates bot loading). onError is therefore effectively dead,
                        // but routes to `loaded` as a belt-and-suspenders.
                        loadingActionNodeNames: {
                          id: 'loadingActionNodeNames',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingActionNodeNames',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingActionNodeNames.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadActionNodeNames,
                                onDoneTarget: '#loadedActionNodeNames',
                                onErrorTarget: '#loadedActionNodeNames',
                                onErrorAssignContext({ ctx, error }) {
                                  recordError(ctx.errors, error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.actionNodeNames = data.actionNodeNames;
                                  }
                                },
                              }),
                            },
                            loaded: {
                              id: 'loadedActionNodeNames',
                              type: 'final',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadedActionNodeNames.loaded',
                                  info: { ctx, event },
                                });
                              },
                            },
                          },
                        },
                        loadingVersions: {
                          id: 'loadingVersions',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingVersions',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingVersions.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadBotVersions,
                                onDoneTarget: '#loadedVersions',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  recordError(ctx.errors, error);
                                  ctx.solutionOpenFailure =
                                    failureFromLoadedSolution({
                                      solutions: ctx.solutions,
                                      botName: ctx.botName,
                                      botIdURLParam: ctx.botIdURLParam,
                                    });
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.solutionOpenFailure = null;
                                    ctx.solutionOpenAttempt = null;
                                    ctx.solutionOpenFailureDismissed = false;
                                    const {
                                      versions,
                                      deployedVersions,
                                      draftVersion,
                                      envs,
                                      selectedBot,
                                      draftData,
                                      draftVersionSolutionData,
                                      solutionVersions,
                                      corpusSettings,
                                      solutionVersionPsId,
                                    } = data;

                                    ctx.draft = draftData;
                                    ctx.versions = versions || [];
                                    ctx.deployedVersions = deployedVersions;
                                    ctx.draftVersion = draftVersion;
                                    ctx.solutionVersionPsId =
                                      solutionVersionPsId;
                                    ctx.solutionVersionPsSolutionId =
                                      selectedBot?.uniqueId ?? null;
                                    ctx.envs = envs;
                                    ctx.selectedBot = selectedBot;
                                    if (ctx.solutions) {
                                      ctx.solutions = ctx.solutions.map((bot) =>
                                        // Matched on the solution uuid: `botSolutionId`
                                        // is null for every v2 solution, so comparing
                                        // it would match all of them at once and
                                        // overwrite each with the selected one.
                                        bot.id === selectedBot.uniqueId
                                          ? {
                                              ...selectedBot,
                                              ...bot,
                                              versions: selectedBot.versions,
                                              draftVersion:
                                                selectedBot.draftVersion,
                                            }
                                          : bot
                                      );
                                    }
                                    ctx.draftVersionSolutionData =
                                      draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                                    ctx.solutionVersions =
                                      solutionVersions as ProStudioContext['solutionVersions'];
                                    ctx.corpusSettings = corpusSettings;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error fetching solutions data',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedVersions',
                              type: 'final',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingVersions.loaded',
                                  info: { ctx, event },
                                });
                              },
                            },
                          },
                        },
                        loadingPypes: {
                          id: 'loadingPypes',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingPypes',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingPypes.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadBotPypes,
                                onDoneTarget: '#loadedPypes',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  recordError(ctx.errors, error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.botPypes = data.botPypes;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error loading pypes',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedPypes',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingPypes.loaded',
                                  info: { ctx, event },
                                });
                              },
                              type: 'final',
                            },
                          },
                        },
                      },
                    },
                    setSelectedSolution: {
                      id: 'setSelectedSolution',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'setSelectedSolution',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: setSelectedSolution,
                        onDoneTarget: `#botFlow`,
                        onErrorTarget: '#botFlowError',
                        onDoneAssignContext({ ctx, data }) {
                          if (!data) {
                            return ctx;
                          }

                          ctx.selectedBot = data.selectedSolution;
                          ctx.botId = data.selectedSolution?.id;
                          ctx.botName = data.selectedSolution?.botName;
                          ctx.solutionOpenFailure = null;
                          ctx.solutionOpenAttempt = null;
                          ctx.solutionOpenFailureDismissed = false;
                          data.cb?.();
                        },
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                          ctx.solutionOpenFailure = failureFromOpenAttempt(
                            ctx.solutionOpenAttempt
                          );
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error setting selected bot',
                          }),
                        ],
                      }),
                    },
                    validatingDraftFlow: {
                      id: 'validatingDraftFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'validatingDraftFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: validateVersion,
                        onDoneTarget: `#botDataLoaded`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error validating draft',
                          }),
                        ],
                      }),
                    },
                    botDataLoaded: {
                      id: 'botDataLoaded',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botDataLoaded',
                          info: { ctx, event },
                        });
                      },
                      always: [
                        {
                          target: '#checkingRequiredData',
                        },
                      ],
                    },
                    creatingBotFlow: {
                      id: 'creatingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createBot,
                        onDoneTarget: '#botFlow',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.solutionOpenFailure = null;
                            ctx.solutionOpenAttempt = null;
                            ctx.solutionOpenFailureDismissed = false;
                            // ctx.solutions = undefined;
                            ctx.botName = data.botName;
                            ctx.selectedBot = data.selectedSolution;

                            const newSolutions =
                              data.solutions as ProStudioContext['solutions'];
                            ctx.solutions = newSolutions?.map((newSol) => {
                              const existing = ctx.solutions?.find(
                                // By uuid, not botSolutionId: the latter is null for
                                // every v2 solution, so it would match the first one
                                // regardless of which solution this actually is.
                                (s) => s.id === newSol.id
                              );
                              return existing
                                ? { ...existing, ...newSol }
                                : newSol;
                            });
                          }
                        },
                        onDoneActions: [
                          (ctx, event) => {
                            const { data } = event;
                            if (data) {
                              data.cb(
                                `/${ctx.project?.id}/${data.selectedSolution.uniqueId}`
                              );
                            }
                          },
                          showToastMessage({
                            text: 'Solution created successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating solution',
                          }),
                        ],
                      }),
                    },
                    deletingBotFlow: {
                      id: 'deletingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteBot,
                        onDoneTarget: '#loadingBots',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            data.cb?.();

                            ctx.solutions = undefined;
                            ctx.botName = undefined;
                            ctx.channelId = undefined;
                            ctx.deployedVersions = null;
                            ctx.draft = null;
                            ctx.draftVersion = null;
                            ctx.envs = undefined;
                            ctx.botPypes = null;
                            ctx.defaultMessagesPypes = null;
                            ctx.selectedBot = undefined;
                            ctx.draftVersionSolutionData = null;
                            ctx.solutionVersions = [];
                            ctx.corpusSettings = null;
                            ctx.versions = undefined;
                            ctx.botIdURLParam = undefined;
                            ctx.botId = undefined;
                            ctx.solutionOpenFailure = null;
                            ctx.solutionOpenAttempt = null;
                            ctx.solutionOpenFailureDismissed = false;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting solution',
                          }),
                        ],
                      }),
                    },
                    publishingBotFlow: {
                      id: 'publishingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'publishingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: publishBotVersion,
                        onDoneTarget: '#botDataLoading',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx }) {
                          ctx.selectedBot = undefined;
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error publishing solution version',
                          }),
                        ],
                      }),
                    },
                    loadingPublishModalDataFlow: {
                      id: 'loadingPublishModalDataFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingPublishModalDataFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadPublishModalData,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#idle',
                        onErrorAssignContext({ ctx }) {
                          ctx.isLoadingPublishModalData = false;
                          // Don't leave a previous survey's links/envs in place after a
                          // failed refresh — they'd be wrongly pre-selected and enabled.
                          ctx.surveyLinkedSolutions = undefined;
                          ctx.publishModalSolutionEnvs = undefined;
                        },
                        onDoneAssignContext({ ctx, data }) {
                          ctx.isLoadingPublishModalData = false;
                          if (!data) return;
                          ctx.surveyLinkedSolutions =
                            data.surveyLinkedSolutions;
                          ctx.publishModalSolutionEnvs = data.solutionEnvs;
                        },
                      }),
                    },
                    updatingBotFlow: {
                      id: 'updatingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: changeBotType,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data: patchedBot }) {
                          if (patchedBot) {
                            ctx.selectedBot = patchedBot;
                            ctx.solutions = applySwitchedSolutionType(
                              ctx.solutions,
                              patchedBot
                            );
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution type updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating solution type',
                          }),
                        ],
                      }),
                    },
                    updatingSolutionFlow: {
                      id: 'updatingSolutionFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingSolutionFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateSolution,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draftVersionSolutionData =
                              data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                            if (data.cb) {
                              data.cb(
                                data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData']
                              );
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating solution',
                          }),
                        ],
                      }),
                    },
                    refetchingSolutionDataFlow: {
                      id: 'refetchingSolutionDataFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'refetchingSolutionDataFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: refetchSolutionData,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draftVersionSolutionData =
                              data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                            if (data.cb) {
                              data.cb(
                                data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData']
                              );
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution data refreshed',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error refreshing solution data',
                          }),
                        ],
                      }),
                    },
                    downloadingVersionFileFlow: {
                      id: 'downloadingVersionFileFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'downloadingVersionFileFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: downloadVersionFile,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneActions: [],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error downloading solution files',
                          }),
                        ],
                      }),
                    },
                    deleteAssetFlow: {
                      id: 'deleteAssetFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deleteAssetFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteAsset,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedBot;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Asset deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting asset',
                          }),
                        ],
                      }),
                    },
                    updatingAssetsFlow: {
                      id: 'updatingAssetsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingAssetsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateAssets,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedBot;
                          }
                          // Always settle the caller's cb exactly once — even
                          // when the bot has no assets array — so a caller
                          // awaiting it cannot hang. The onError path settles
                          // cb([]) inside the invoke.
                          if (data?.cb) {
                            data.cb(
                              (data.selectedBot?.assets ?? []) as {
                                id: string;
                                url: string;
                              }[]
                            );
                          }
                        },
                      }),
                    },
                    deletingScriptFlow: {
                      id: 'deletingScriptFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingScriptFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteScript,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draft = data.draft;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Script deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting script',
                          }),
                        ],
                      }),
                    },
                    loadingScriptFlow: {
                      id: 'loadingScriptFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingScriptFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: getFileData,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneActions: [
                          {
                            type: 'downloadScript',
                            exec(_ctx, event) {
                              if (!event.data) {
                                return;
                              }

                              console.log('event.data.type', event.data.type);

                              if (event.data.type === 'downloadScript') {
                                downloadPythonFile(
                                  event.data.fileData,
                                  event.data.fileName
                                );
                              }
                              if (
                                event.data.type === 'editScript' &&
                                event.data.cb
                              ) {
                                event.data.cb(event.data.fileData);
                              }
                            },
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error downloading script',
                          }),
                        ],
                      }),
                    },
                    creatingChannelFlow: {
                      id: 'creatingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.botPypes = data.botPypes;
                            invalidateChannelDerivedCaches(ctx);
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel created successfully',
                            type: 'success',
                          }),
                          (ctx, event) => {
                            // Only track if user has consented to analytics
                            const analytics = getAnalytics();
                            if (
                              analytics &&
                              typeof analytics.has_opted_in_capturing ===
                                'function' &&
                              analytics.has_opted_in_capturing() &&
                              typeof analytics.capture === 'function' &&
                              'data' in event
                            ) {
                              const data = event.data as {
                                channelId?: string;
                                channelName?: string;
                                pypeId?: string;
                                pypeName?: string;
                              };
                              const userEmail =
                                userService.state.context.user?.email;

                              analytics.capture('channel_created', {
                                user_email: userEmail,
                                channel_id: data.channelId,
                                channel_name: data.channelName,
                                pype_id: data.pypeId,
                                pype_name: data.pypeName,
                                bot_name: ctx.botName,
                                project_id: ctx.project?.id,
                              });
                            }
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating channel',
                          }),
                        ],
                      }),
                    },
                    deletingChannelFlow: {
                      id: 'deletingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.botPypes = data.botPypes;
                            invalidateChannelDerivedCaches(ctx);
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting channel',
                          }),
                        ],
                      }),
                    },
                    loadingLiveAgentPlatformsFlow: {
                      id: 'loadingLiveAgentPlatformsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingLiveAgentPlatformsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadCustomerPypes,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                          markChannelSettingsLoad(
                            ctx,
                            'liveAgentPlatforms',
                            false
                          );
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeConnections = data.pypeConnections;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                            ctx.agentRoutingSchemas = data.agentRoutingSchemas;
                          }
                          markChannelSettingsLoad(
                            ctx,
                            'liveAgentPlatforms',
                            Boolean(data)
                          );
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading live agent platforms',
                          }),
                        ],
                      }),
                    },
                    creatingLiveAgentPlatformFlow: {
                      id: 'creatingLiveAgentPlatformFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingLiveAgentPlatformFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createLiveAgentPlatform,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeConnections = data.pypeConnections;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Live agent platform created successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating live agent platform',
                          }),
                        ],
                      }),
                    },
                    updatingLiveAgentPlatformFlow: {
                      id: 'updatingLiveAgentPlatformFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingLiveAgentPlatformFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateLiveAgentPlatform,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeConnections = data.pypeConnections;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Live agent platform updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating live agent platform',
                          }),
                        ],
                      }),
                    },
                    deletingLiveAgentPlatformFlow: {
                      id: 'deletingLiveAgentPlatformFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingLiveAgentPlatformFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteLiveAgentPlatform,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeConnections = data.pypeConnections;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Live agent platform deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting live agent platform',
                          }),
                        ],
                      }),
                    },
                    loadingPypeRuleGroupsFlow: {
                      id: 'loadingPypeRuleGroupsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingPypeRuleGroupsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadPypeRuleGroups,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                          markChannelSettingsLoad(ctx, 'ruleGroups', false);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeRuleGroups = data.pypeRuleGroups;
                            ctx.pypeRuleGroupChannels = data.channels;
                          }
                          markChannelSettingsLoad(
                            ctx,
                            'ruleGroups',
                            Boolean(data)
                          );
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading masking rule sets',
                          }),
                        ],
                      }),
                    },
                    creatingPypeRuleGroupFlow: {
                      id: 'creatingPypeRuleGroupFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingPypeRuleGroupFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createPypeRuleGroup,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeRuleGroups = data.pypeRuleGroups;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Rule set created successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating rule set',
                          }),
                        ],
                      }),
                    },
                    updatingPypeRuleGroupFlow: {
                      id: 'updatingPypeRuleGroupFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingPypeRuleGroupFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updatePypeRuleGroup,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeRuleGroups = data.pypeRuleGroups;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Rule set updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating rule set',
                          }),
                        ],
                      }),
                    },
                    deletingPypeRuleGroupFlow: {
                      id: 'deletingPypeRuleGroupFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingPypeRuleGroupFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deletePypeRuleGroup,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeRuleGroups = data.pypeRuleGroups;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Rule set deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting rule set',
                          }),
                        ],
                      }),
                    },
                    assigningPypeRuleGroupFlow: {
                      id: 'assigningPypeRuleGroupFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'assigningPypeRuleGroupFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: assignPypeRuleGroup,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeRuleGroups = data.pypeRuleGroups;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Masking rule set updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating masking rule set',
                          }),
                        ],
                      }),
                    },
                    assigningPypeConnectionFlow: {
                      id: 'assigningPypeConnectionFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'assigningPypeConnectionFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: assignPypeConnection,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.pypeConnections = data.pypeConnections;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Live agent platform updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating live agent platform',
                          }),
                        ],
                      }),
                    },
                    loadingApiCredentialsFlow: {
                      id: 'loadingApiCredentialsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingApiCredentialsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadApiCredentials,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                          markChannelSettingsLoad(ctx, 'apiCredentials', false);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.apiCredentials = data.apiCredentials;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                          }
                          markChannelSettingsLoad(
                            ctx,
                            'apiCredentials',
                            Boolean(data)
                          );
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading API credentials',
                          }),
                        ],
                      }),
                    },
                    loadingChannelApiCredentialsFlow: {
                      id: 'loadingChannelApiCredentialsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingChannelApiCredentialsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadChannelApiCredentials,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                          markChannelSettingsLoad(ctx, 'apiCredentials', false);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.channelApiCredentials =
                              data.channelApiCredentials;
                          }
                          markChannelSettingsLoad(
                            ctx,
                            'apiCredentials',
                            Boolean(data)
                          );
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading API credentials',
                          }),
                        ],
                      }),
                    },
                    creatingApiCredentialFlow: {
                      id: 'creatingApiCredentialFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingApiCredentialFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createApiCredential,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.apiCredentials = data.apiCredentials;
                            // A key added or removed here belongs to one of the
                            // project's pypes, and the channel form caches THAT
                            // pype's keys in its own slice. Drop it, or the
                            // channel's Reporting API block keeps showing the
                            // pre-CRUD list until a reload (PSTUDIO-4253).
                            ctx.channelApiCredentials = null;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                            // Hand the created key + secret to the modal so it
                            // can reveal them once before closing.
                            data.cb?.(data.createdKey);
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'API credential created successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating API credential',
                          }),
                        ],
                      }),
                    },
                    updatingApiCredentialFlow: {
                      id: 'updatingApiCredentialFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingApiCredentialFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateApiCredential,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.apiCredentials = data.apiCredentials;
                            // A key added or removed here belongs to one of the
                            // project's pypes, and the channel form caches THAT
                            // pype's keys in its own slice. Drop it, or the
                            // channel's Reporting API block keeps showing the
                            // pre-CRUD list until a reload (PSTUDIO-4253).
                            ctx.channelApiCredentials = null;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'API credential updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating API credential',
                          }),
                        ],
                      }),
                    },
                    deletingApiCredentialFlow: {
                      id: 'deletingApiCredentialFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingApiCredentialFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteApiCredential,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.apiCredentials = data.apiCredentials;
                            // A key added or removed here belongs to one of the
                            // project's pypes, and the channel form caches THAT
                            // pype's keys in its own slice. Drop it, or the
                            // channel's Reporting API block keeps showing the
                            // pre-CRUD list until a reload (PSTUDIO-4253).
                            ctx.channelApiCredentials = null;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'API credential deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting API credential',
                          }),
                        ],
                      }),
                    },
                    loadingErrorHandlingFlow: {
                      id: 'loadingErrorHandlingFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingErrorHandlingFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadErrorHandling,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.errorHandling = data.errorHandling;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                          }
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading error handling settings',
                          }),
                        ],
                      }),
                    },
                    loadingDefaultMessagesFlow: {
                      id: 'loadingDefaultMessagesFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingDefaultMessagesFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: loadDefaultMessages,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.defaultMessagesPypes =
                              data.defaultMessagesPypes;
                          }
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error loading default messages',
                          }),
                        ],
                      }),
                    },
                    updatingDefaultMessagesFlow: {
                      id: 'updatingDefaultMessagesFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingDefaultMessagesFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateDefaultMessages,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.defaultMessagesPypes =
                              data.defaultMessagesPypes;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Default messages updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating default messages',
                          }),
                        ],
                      }),
                    },
                    updatingErrorHandlingFlow: {
                      id: 'updatingErrorHandlingFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingErrorHandlingFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateErrorHandling,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.errorHandling = data.errorHandling;
                            ctx.liveAgentPypes = data.liveAgentPypes;
                            data.cb?.();
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Error handling settings updated successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating error handling settings',
                          }),
                        ],
                      }),
                    },
                    togglingAnalyticsFlow: {
                      id: 'togglingAnalyticsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'togglingAnalyticsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: toggleAnalyticsForChannel,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.botPypes = data.botPypes;
                          }
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Failed to update analytics settings',
                          }),
                        ],
                      }),
                    },
                    updatingChannelFlow: {
                      id: 'updatingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data && 'botPypes' in data) {
                            ctx.botPypes = data.botPypes;
                          }
                          // A rename changes the channel name these screens
                          // display, so they must re-load like on create/delete.
                          // A styling-only save changes none of it, and dropping
                          // the slices there made the next Channel Settings
                          // visit pay for the whole load chain again — the cost
                          // PSTUDIO-4253 is about. The invoke decides, while it
                          // can still see the pre-update channel.
                          if (!data || data.needsCacheInvalidation) {
                            invalidateChannelDerivedCaches(ctx);
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel updated successfully',
                            type: 'success',
                          }),
                          (ctx, event) => {
                            // Only track if user has consented to analytics
                            const analytics = getAnalytics();
                            if (
                              analytics &&
                              typeof analytics.has_opted_in_capturing ===
                                'function' &&
                              analytics.has_opted_in_capturing() &&
                              typeof analytics.capture === 'function' &&
                              'data' in event
                            ) {
                              const data = event.data as {
                                channelId?: string;
                                channelName?: string;
                                pypeId?: string;
                                pypeName?: string;
                              };
                              const userEmail =
                                userService.state.context.user?.email;

                              analytics.capture('channel_updated', {
                                user_email: userEmail,
                                channel_id: data.channelId,
                                channel_name: data.channelName,
                                pype_id: data.pypeId,
                                pype_name: data.pypeName,
                                bot_name: ctx.botName,
                                project_id: ctx.project?.id,
                              });
                            }
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating channel',
                          }),
                        ],
                      }),
                    },
                    updatingSolutionNameFlow: {
                      id: 'updatingSolutionNameFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingSolutionNameFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateSolutionName,
                        // PSTUDIO-3596: optimistic + refetch. onDoneAssignContext
                        // patches ctx optimistically for instant feedback; then
                        // #loadingBots refetches getSolutions so ctx.solutions is
                        // reconciled with the backend (mirrors deletingBotFlow).
                        // The optimistic ctx.solutions write is authoritatively
                        // overwritten by that refetch — kept for the brief pre-
                        // refetch window; ctx.selectedBot is NOT refetched, so its
                        // optimistic update is the source of the fresh displayName
                        // on the current Versions page.
                        onDoneTarget: '#loadingBots',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedSolution;
                            if (data.solutions) {
                              ctx.solutions = data.solutions;
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution name updated successfully',
                            type: 'success',
                          }),
                        ],
                      }),
                    },
                    uploadingCsvFileFlow: {
                      id: 'uploadingCsvFileFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'uploadingCsvFileFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: reloadNodes,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          recordError(ctx.errors, error);
                        },
                        // PSTUDIO-4122: the success toast used to be raised
                        // here, gated on `isReloadApplied`. It now lives in
                        // `use-upload-csv.ts`. This action ran on `landed`
                        // alone — a flag computed BEFORE the grouping graft —
                        // so an import whose groups were dropped still announced
                        // "CSV successfully imported". The hook is the layer
                        // that knows the graft's outcome, and the only one of
                        // the two with translations.
                        //
                        // A dropped reload still raises nothing: the upload
                        // itself succeeded against the version it was issued
                        // for, and `belongsToCurrentVersion` already logs a
                        // warning (PSTUDIO-4103). The silence is chosen.
                        //
                        // `isReloadApplied` stays exported and unit-tested —
                        // `reload-nodes.ts` still computes the flag `cb`
                        // receives. Nothing in this state branches on it.
                        onDoneActions: [],
                        onErrorActions: [
                          showToastMessage({
                            i18nKey: `${WORKFLOWS_NS}:csv_import.toast.error`,
                          }),
                        ],
                      }),
                    },
                    reloadingNodesFlow: {
                      id: 'reloadingNodesFlow',
                      entry: (
                        ctx: ProStudioContext,
                        event: ProStudioEvents
                      ) => {
                        logOnEntry({
                          name: 'reloadingNodesFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: reloadNodes,
                        // PSTUDIO-3610: silent background cross-tab reload — no
                        // "CSV successfully imported" toast. A failed background
                        // reload returns quietly to idle instead of the CSV error
                        // UI; the cross-tab hook's watchdog frees its in-flight
                        // flag regardless (the invoke's finally() runs cb).
                        onDoneTarget: '#idle',
                        onErrorTarget: '#idle',
                      }),
                    },
                    botFlowError: {
                      id: 'botFlowError',
                      tags: [ErrorTags.xstate],
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botFlowError',
                          info: { ctx, event },
                          color: 'red',
                        });
                      },
                      exit: assign((ctx, _event) => {
                        return {
                          ...ctx,
                          errors: [],
                        };
                      }),
                      after: {
                        100: '#idle',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    accessDenied: {
      id: 'accessDenied',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'accessDenied',
          info: { ctx, event },
        });
      },
      // PSTUDIO-4098. `Root` renders the access screen while the machine is here;
      // the screen assigns the role, waits for it to sync and re-reads the user's
      // projects. A list that now contains the project re-runs the access check,
      // which loads the project like a first visit. Accepted ONLY in this state,
      // so a grant that lands after the user navigated elsewhere is dropped.
      on: {
        projectAccessGranted: {
          target: '#checkingUserInfo',
          actions: assign((ctx, event) => {
            if (event.type !== 'projectAccessGranted') {
              return ctx;
            }
            return contextAfterProjectAccessGranted(ctx, event.userProjects);
          }),
        },
      },
    },
    // PSTUDIO-3671: the open solution/version no longer exists. Clear the
    // bot-scoped context and drop the deleted solution from `solutions` (so the
    // All Solutions list `Root` redirects to does NOT keep showing the row that
    // was just removed — the deleting tab avoids that only because it refetches
    // via #loadingBots; a parked eject has no refetch, so we prune optimistically
    // by the open target, matching what triggered the eject). KEEP the rest of
    // `solutions` so All Solutions can still render. Then show a "solution not
    // found" toast; `Root` redirects to `/:projectId`.
    // This is a TERMINAL leaf state: the machine PARKS here (there is no
    // `always`/`after`), so `solutionNotFound` MUST be in
    // `NON_LOADING_STUDIO_STATES` (see loading-states.ts) or Home's
    // WithLoadingState would blank the list behind the full-page loader. The
    // redirect to the SAME `/:projectId` (botId dropped) is not a "meaningful
    // change" for `syncXstateWithRouteParams`, so it dispatches NO `setParams`
    // and the machine stays parked here until the user next navigates to a
    // solution (a real `setParams` with a botName) — which is fine: the visible
    // page is already the correct All Solutions list.
    solutionNotFound: {
      id: 'solutionNotFound',
      entry: [
        (ctx: ProStudioContext, event: ProStudioEvents) => {
          logOnEntry({
            name: 'solutionNotFound',
            info: { ctx, event },
            color: 'red',
          });
        },
        assign((ctx: ProStudioContext) => ({
          ...ctx,
          // Prune the deleted solution BEFORE the bot-scoped fields below are
          // cleared — read the open target from the current (pre-clear) ctx.
          solutions: removeSolutionFromList({
            solutions: ctx.solutions,
            botName: ctx.botName,
            botIdURLParam: ctx.botIdURLParam,
            botId: ctx.botId,
          }),
          botName: undefined,
          botId: undefined,
          botIdURLParam: undefined,
          channelId: undefined,
          selectedBot: undefined,
          draft: null,
          draftVersion: null,
          solutionVersionPsId: null,
          solutionVersionPsSolutionId: null,
          draftVersionSolutionData: null,
          versions: undefined,
          deployedVersions: null,
          solutionVersions: [],
          corpusSettings: null,
          envs: undefined,
          botPypes: null,
          defaultMessagesPypes: null,
          // This eject already toasts that the solution is gone. The
          // open-failure dialog would name a solution this state just dropped.
          solutionOpenFailure: null,
          solutionOpenAttempt: null,
          solutionOpenFailureDismissed: false,
        })),
        // Use `sendUserMessage` directly (NOT `showToastMessage`): the latter is
        // an invoke-onError action that reads `event.data.message`
        // (CombinedError). The entry event here has no error payload, so a plain
        // fire-and-forget toast is correct (same pattern as
        // load-bot-versions.ts). Hardcoded English matches the machine's other
        // toasts by convention.
        (ctx: ProStudioContext, event: ProStudioEvents) => {
          logOnEntry({
            name: 'solutionNotFoundToast',
            info: { ctx, event },
          });
          sendUserMessage({
            type: 'error',
            text: 'Solution not found. It may have been deleted.',
            autoClose: 3000,
          });
        },
      ],
    },
    error: {
      id: 'error',
      tags: [ErrorTags.xstate],
      entry: (ctx, event) => {
        logOnEntry({
          name: 'error',
          info: { ctx, event },
          color: 'red',
        });
      },
      // Recovery from the previously terminal #error state. `getProjectData`
      // and `loadBots` route onError → '#error', so a single transient backend
      // timeout (admin-backend / Hasura remote-schema / hasura webhook) used
      // to leave the machine wedged here forever: ctx.project never got
      // populated and the HomePage stayed on the "Please select a project"
      // picker for the rest of the session. Re-evaluate access on the next
      // navigation (any setParams from the route loader), and fall back to a
      // delayed retry so we recover even without a navigation event.
      // checkAccess (states.checkingUserInfo.checkAccess) has guarded `always`
      // transitions that route to studio_idle / projectFlow / botFlow /
      // accessDenied depending on the current ctx — no risk of jumping into a
      // success path with empty ctx.
      exit: assign((ctx, _event) => ({ ...ctx, errors: [] })),
      on: {
        setParams: { target: '#checkAccess' },
      },
      after: {
        5000: '#checkAccess',
      },
    },
  },
});
